import { formatDurationSeconds, formatFileSize, tr } from '../services/i18n.js';

// Limites d'upload vidéo et de durée de segment analysé, isolées ici pour être
// testables sans DOM ni env Supabase. La page upload.js consomme ces constantes
// et `validateSegment` pour bloquer proprement et afficher la raison à l'utilisateur.

// Taille max du fichier vidéo brut accepté avant compression/upload.
export const MAX_VIDEO_UPLOAD_BYTES = 50 * 1024 * 1024;

// Durée max (secondes) du segment réellement analysé. Une vidéo plus longue n'est
// pas refusée : seule la fenêtre choisie doit tenir dans cette limite.
export const MAX_ANALYSIS_SEGMENT_SECONDS = 30;

export const ALLOWED_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm', 'mkv', 'avi'];

export const ALLOWED_VIDEO_ACCEPT = ALLOWED_VIDEO_EXTENSIONS.map((ext) => `.${ext}`).join(',');

const ALLOWED_VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/x-m4v',
  'video/quicktime',
  'video/webm',
  'video/matroska',
  'video/x-matroska',
  'video/avi',
  'video/x-msvideo'
]);

// Petite tolérance pour absorber les arrondis de saisie (steps de 0.5s).
const SEGMENT_EPSILON_S = 0.05;

// Formate des octets en taille lisible et localisée (2 décimales).
export function formatMB(bytes) {
  return formatFileSize(bytes);
}

export function videoExtension(name = '') {
  const m = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

export function isAllowedVideoType(file = {}) {
  const ext = videoExtension(file.name);
  const mime = String(file.type || '').split(';')[0].trim().toLowerCase();
  const extAllowed = ALLOWED_VIDEO_EXTENSIONS.includes(ext);
  const mimeAllowed = !mime || mime === 'application/octet-stream' || ALLOWED_VIDEO_MIME_TYPES.has(mime);
  return extAllowed && mimeAllowed;
}

export function detectVideoContainer(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (b.length >= 12) {
    const brand = String.fromCharCode(b[4], b[5], b[6], b[7]);
    if (brand === 'ftyp') return 'iso-bmff';
  }
  if (b.length >= 4 && b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) {
    return 'ebml';
  }
  if (b.length >= 12) {
    const riff = String.fromCharCode(b[0], b[1], b[2], b[3]);
    const avi = String.fromCharCode(b[8], b[9], b[10], b[11]);
    if (riff === 'RIFF' && avi === 'AVI ') return 'avi';
  }
  return null;
}

export async function validateVideoFile(file) {
  if (!file) {
    return { valid: false, message: tr('Sélectionne une vidéo.', 'Select a video.') };
  }
  if (!isAllowedVideoType(file)) {
    return {
      valid: false,
      message: tr('Format vidéo requis', 'Required video format') + `: ${ALLOWED_VIDEO_EXTENSIONS.map((ext) => `.${ext}`).join(', ')}.`
    };
  }
  if (file.size > MAX_VIDEO_UPLOAD_BYTES) {
    return {
      valid: false,
      message: tr(
        `Vidéo trop volumineuse: ${formatMB(file.size)} sélectionnés, limite ${formatMB(MAX_VIDEO_UPLOAD_BYTES)}.`,
        `Video too large: ${formatMB(file.size)} selected, limit ${formatMB(MAX_VIDEO_UPLOAD_BYTES)}.`
      )
    };
  }
  const header = new Uint8Array(await file.slice(0, 32).arrayBuffer());
  if (!detectVideoContainer(header)) {
    return {
      valid: false,
      message: tr(
        'Fichier vidéo invalide : le contenu ne correspond pas à un format vidéo accepté.',
        'Invalid video file: the content does not match an accepted video format.'
      )
    };
  }
  return { valid: true, message: '' };
}

// Valide la fenêtre temporelle du segment analysé. Renvoie un objet uniforme
// { valid, message, segment } : `segment` est { start, end } si valide, sinon null.
// `message` est toujours destiné à l'utilisateur (seg-hint).
export function validateSegment(start, end, videoDuration, maxSeconds = MAX_ANALYSIS_SEGMENT_SECONDS) {
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return { valid: false, message: tr('Indique un début et une fin en secondes.', 'Enter a start and end time in seconds.'), segment: null };
  }
  if (start < 0) {
    return { valid: false, message: tr('Le début doit être ≥ 0.', 'Start must be >= 0.'), segment: null };
  }
  if (end <= start) {
    return { valid: false, message: tr('La fin doit être strictement après le début.', 'End must be strictly after start.'), segment: null };
  }
  if (Number.isFinite(videoDuration) && videoDuration > 0 && end > videoDuration + SEGMENT_EPSILON_S) {
    return { valid: false, message: tr(`La fin dépasse la durée de la vidéo (${formatDurationSeconds(videoDuration)}).`, `End exceeds the video duration (${formatDurationSeconds(videoDuration)}).`), segment: null };
  }
  const duration = end - start;
  if (duration > maxSeconds + SEGMENT_EPSILON_S) {
    return {
      valid: false,
      message: tr(
        `Segment trop long : ${formatDurationSeconds(duration)} pour un maximum de ${formatDurationSeconds(maxSeconds, { fractionDigits: 0 })}. Réduis la fenêtre.`,
        `Segment too long: ${formatDurationSeconds(duration)} for a maximum of ${formatDurationSeconds(maxSeconds, { fractionDigits: 0 })}. Shorten the window.`
      ),
      segment: null
    };
  }
  return {
    valid: true,
    message: tr(
      `Segment analysé : ${formatDurationSeconds(start)} → ${formatDurationSeconds(end)} (${formatDurationSeconds(duration)}).`,
      `Analyzed segment: ${formatDurationSeconds(start)} → ${formatDurationSeconds(end)} (${formatDurationSeconds(duration)}).`
    ),
    segment: { start, end }
  };
}
